import { z } from 'zod';
import { lastSeenOnlineSchema, userMetaPresenceSchema } from './user.meta.presence.model';
import { userMetaLocationSchema } from './user.meta.location.model';
import { userMetaChannelsSchema } from './user.meta.channels.model';
import { referralAttributionSchema } from '../referral/referral.attribution.model';

/**
 * `User.meta` — agent-facing signal layer. See
 * `docs/ai/user-signals.md` for the full read model and
 * `docs/ai/proactive-outreach.md` for the decision engine that
 * consumes it.
 *
 * This block is optional at the schema level (so pre-existing
 * users read cleanly) and gated at the DTO layer by
 * `stripUserMeta` on non-privileged reads — see
 * `apps/backend/src/app/user/user.dto.service.ts`.
 *
 * Adding a new top-level signal here is deliberate: keep the
 * exposure gate in exactly one place, and don't leak partial
 * signals into public read paths.
 */
export const userMetaSchema = z.object({
  /**
   * IANA timezone the user is (probably) in — e.g. `Asia/Riyadh`.
   * Used by the outreach decision engine for DND windows and by
   * agent replies that format timestamps in the user's local time.
   *
   * Cross-app signal — a single value regardless of which app the
   * user is on. Falls back to `Asia/Riyadh` for KSA users when
   * unset (see `docs/ai/user-signals.md`).
   */
  timezone: z.string().trim().optional(),
  /**
   * Most-recent "user was online" heartbeat across ANY app. This
   * is the single cross-app answer to "is this user awake?" — the
   * per-app entries in `meta.presence.apps[]` are the per-surface
   * detail. Shared shape with `Org.meta.lastSeenOnline`.
   */
  lastSeenOnline: lastSeenOnlineSchema.optional(),
  presence: userMetaPresenceSchema.optional(),
  location: userMetaLocationSchema.optional(),
  channels: userMetaChannelsSchema.optional(),
  /**
   * Which referral link brought this user in. Same shape and lifecycle
   * as `Org.meta.referral`; the copy that matters for a freelancer or an
   * individual customer, whose Billings are paid by the User rather than
   * an Org. Written once at signup.
   */
  referral: referralAttributionSchema.optional(),
  // NOTE: OS permissions live PER (app, platform) inside
  // `presence.apps[i].permissions` — the same iPhone-carrier-app can
  // have location granted while the same user's Android-customer-app
  // has it denied. See `docs/ai/user-signals.md`.
});
export type UserMeta = z.infer<typeof userMetaSchema>;
